import type {BlankType, OtkBlank} from "shared"
import type {CommissionMember} from "@/lib/actRequisites"
import {formatUaDate as formatDateOnlyUa} from "@/lib/utils"

// warehouse.md's status Tabs — real `BlankStatus` values (`shared`'s
// `otk-blank.ts`), Ukrainian labels per the page spec. `RESERVED_BY_GSC` and
// `REJECTED` have no tab (see warehouse.md's Design section: the customer
// decided against a "ЗАРЕЗЕРВОВАНО ГСЦ" tab, and П3 removed забракування
// entirely) — a blank in either status is simply never fetched, same as
// documents.md's unreachable statuses.
export const warehouseTabStatuses = [
  "available",
  "annulled",
  "used",
  "destroyed",
] as const
export type WarehouseTabStatus = (typeof warehouseTabStatuses)[number]

export function isWarehouseTabStatus(
  value: string | null
): value is WarehouseTabStatus {
  return (warehouseTabStatuses as readonly string[]).includes(value ?? "")
}

export const warehouseTabLabels: Record<WarehouseTabStatus, string> = {
  available: "Доступні",
  annulled: "Анульовані",
  used: "Використані",
  destroyed: "Знищені",
}

export const warehouseTabToBackendStatus = {
  available: "IN_STOCK",
  annulled: "ANNULLED",
  used: "USED",
  destroyed: "DESTROYED",
} as const

// warehouse.md's «Показувати» type filter — "Всі" carries no `type` param at
// all (see `getOtkBlanksPage`), matching the query's own optional `type`.
export const warehouseTypeFilters = ["all", "physical", "electronic"] as const
export type WarehouseTypeFilter = (typeof warehouseTypeFilters)[number]

export function isWarehouseTypeFilter(
  value: string | null
): value is WarehouseTypeFilter {
  return (warehouseTypeFilters as readonly string[]).includes(value ?? "")
}

export const warehouseTypeFilterLabels: Record<WarehouseTypeFilter, string> = {
  all: "Всі",
  physical: "Протокол ОТК",
  electronic: "Електронний протокол",
}

export const warehouseTypeFilterToBackend: Record<
  WarehouseTypeFilter,
  BlankType | undefined
> = {
  all: undefined,
  physical: "PROTOCOL_FORM",
  electronic: "ELECTRONIC_OTK_DOCUMENT",
}

// warehouse.md's "Тип" column display — the real `BlankType` enum
// (`spetsproduktsiya.md`'s grounding) rendered with the page's own shorter
// labels, same two values as the type filter above.
export const blankTypeLabels: Record<BlankType, string> = {
  PROTOCOL_FORM: "Протокол ОТК",
  ELECTRONIC_OTK_DOCUMENT: "Електронний протокол",
}

// warehouse.md's `Blank.status` display — the shared `Status` badge takes
// the label text itself (see `lib/status.ts`'s `"АНУЛ"` substring rule), so
// this is the one place the real English `BlankStatus` enum is translated.
export const blankStatusLabels: Record<string, string> = {
  IN_STOCK: "ДОСТУПНИЙ",
  USED: "ВИКОРИСТАНО",
  REJECTED: "ЗАБРАКОВАНО",
  ANNULLED: "АНУЛЬОВАНО",
  DESTROYED: "ЗНИЩЕНО",
  RESERVED_BY_GSC: "ЗАРЕЗЕРВОВАНО ГСЦ",
}

/**
 * warehouse.md's eligibility rule for the destruction procedure, mode
 * `annulled`: physical type, status exactly `ANNULLED`. `punkt` isn't
 * checked here — see the gap note on `BlankRow` below.
 */
export function isAnnulledDestructionCandidate(blank: OtkBlank): boolean {
  return blank.type === "PROTOCOL_FORM" && blank.status === "ANNULLED" && blank.destructionActId === null
}

/**
 * warehouse.md's eligibility rule for the destruction procedure, mode
 * `availableDamaged`: physical type, status exactly `IN_STOCK`.
 */
export function isAvailableDamagedCandidate(blank: OtkBlank): boolean {
  return blank.type === "PROTOCOL_FORM" && blank.status === "IN_STOCK" && blank.destructionActId === null
}

// warehouse.md's «Анулювати документ» row action, «Використані» only —
// gated in the page spec by the centralized `annulActionAvailability` check
// over the *linked document* (type/status/KEP-deadline/authorization). The
// real `OtkBlank` only carries `documentId`/`documentNumber`/`signedAt`, not
// the document's own status or a KEP timestamp, so that full check cannot be
// reproduced here — same "no backend integration yet" scoping
// `AnnulDialog.tsx` already documents for the plain documents list. This
// page offers the action whenever a document is linked at all; a real
// deadline/status gate is a follow-up once the blank list exposes it (or
// once this action calls the same endpoint documents.md's row action would).
export function canOfferAnnul(blank: OtkBlank): boolean {
  return blank.documentId !== null
}

// warehouse.md's date columns render `dd.MM.yyyy` — `lib/utils.ts`'s shared
// `formatUaDate` already does that for a date-only `yyyy-MM-dd` string
// (documents.md's own convention); this wraps it to also accept a full ISO
// timestamp (`OtkBlank.signedAt`/`updatedAt`, otk-be's own convention, unlike
// the wireframe's date-only mock) and a nullable value, rather than
// duplicating a second formatter.
export function formatUaDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const datePart = iso.split("T")[0]
  if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return "—"
  return formatDateOnlyUa(datePart)
}

/** `ActPrintSheet`/`ActViewDialog`'s "посада, ПІБ" formatting — ported verbatim. */
export function formatCommissionMember(member: CommissionMember): string {
  const role = member.role.trim()
  const name = member.name.trim()
  if (!role) return name
  if (!name) return role
  return `${role}, ${name}`
}
